// ── Welle D3b: reine Layout-Logik des Heute-Boards (ohne React) ──
// Server-Format (D3a, lib/dashboard-widgets.js): PC { id, x, y, w, h }, Handy { id, groesse }.
// react-grid-layout benutzt „i“ statt „id“ und hängt eigene Felder an — beim Speichern
// wird zurück auf das Server-Format abgebildet.

export type Groesse = 'klein' | 'gross';
export type PcItem = { id: string; x: number; y: number; w: number; h: number };
export type MobilItem = { id: string; groesse: Groesse };
export type PcLayout = { widgets: PcItem[]; ausgeblendet: string[] };
export type MobilLayout = { widgets: MobilItem[]; ausgeblendet: string[] };
export type Modell = 'pc' | 'mobil';
export type LayoutVon<M extends Modell> = M extends 'pc' ? PcLayout : MobilLayout;

export type WidgetDef = {
  id: string;
  pc: { standard: [number, number]; min: [number, number]; max: [number, number] };
  handy: { standard: Groesse; erlaubt: Groesse[] };
  sichtbar: { pc: boolean; handy: boolean };
};
export type Katalog = {
  raster: { spalten: number; max_zeilen: number };
  widgets: WidgetDef[];
  standard: { pc: PcLayout; mobil: MobilLayout };
  hinweise: string[];
};

export type RglItem = {
  i: string; x: number; y: number; w: number; h: number;
  minW?: number; minH?: number; maxW?: number; maxH?: number;
};

export const SPALTEN = 4;
export const MAX_ZEILEN = 40;

const ueberlappt = (a: PcItem, b: PcItem) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** Vertikales Nachrücken wie react-grid-layout (verticalCompactor): von oben nach unten, je so hoch wie möglich. */
export function kompaktiere(items: PcItem[]): PcItem[] {
  const sortiert = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  const fertig: PcItem[] = [];
  for (const it of sortiert) {
    const neu = { ...it, x: Math.max(0, Math.min(it.x, SPALTEN - it.w)) };
    const kollidiert = (y: number) => fertig.some(f => ueberlappt(f, { ...neu, y }));
    while (neu.y > 0 && !kollidiert(neu.y - 1)) neu.y--;
    while (kollidiert(neu.y)) neu.y++;
    fertig.push(neu);
  }
  // Reihenfolge der Eingabe beibehalten (stabile Keys/Animationen)
  return items.map(it => fertig.find(f => f.id === it.id)!);
}

/** Erste freie Stelle (Zeile für Zeile, links nach rechts), an der w×h ohne Überlappung passt. */
export function ersteFreieStelle(items: PcItem[], w: number, h: number): { x: number; y: number } {
  for (let y = 0; y < MAX_ZEILEN; y++) {
    for (let x = 0; x + w <= SPALTEN; x++) {
      const probe = { id: '_', x, y, w, h };
      if (!items.some(it => ueberlappt(it, probe))) return { x, y };
    }
  }
  return { x: 0, y: items.reduce((m, it) => Math.max(m, it.y + it.h), 0) };
}

/** Größenstufen für das ⋯-Menü: Klein = min, Mittel = Standard, Groß = max (auf 4 Spalten begrenzt). */
export function groessenStufen(def: WidgetDef): { key: 'klein' | 'mittel' | 'gross'; label: string; w: number; h: number }[] {
  const begrenzt = ([w, h]: [number, number]) => ({ w: Math.min(w, SPALTEN), h });
  const stufen = [
    { key: 'klein' as const,  label: 'Klein',  ...begrenzt(def.pc.min) },
    { key: 'mittel' as const, label: 'Mittel', ...begrenzt(def.pc.standard) },
    { key: 'gross' as const,  label: 'Groß',   ...begrenzt(def.pc.max) },
  ];
  // Gleiche Größen nur einmal anbieten (z. B. Diktat: Standard = Min)
  return stufen.filter((s, i) => stufen.findIndex(t => t.w === s.w && t.h === s.h) === i);
}

/** Größe eines Widgets ändern: x so weit nach links, dass es in 4 Spalten passt; Rest rückt nach. */
export function aendereGroesse(items: PcItem[], id: string, w: number, h: number): PcItem[] {
  const geaendert = items.map(it => it.id !== id ? it : { ...it, w, h, x: Math.max(0, Math.min(it.x, SPALTEN - w)) });
  const ziel = geaendert.find(it => it.id === id)!;
  // Überlappte Nachbarn unter das geänderte Widget schieben, dann nachrücken
  const verschoben = geaendert.map(it => it.id !== id && ueberlappt(it, ziel) ? { ...it, y: ziel.y + ziel.h } : it);
  return kompaktiere(verschoben);
}

export const zuRgl = (items: PcItem[], defs: Map<string, WidgetDef>): RglItem[] =>
  items.map(it => {
    const d = defs.get(it.id);
    return {
      i: it.id, x: it.x, y: it.y, w: it.w, h: it.h,
      minW: d?.pc.min[0], minH: d?.pc.min[1], maxW: d ? Math.min(d.pc.max[0], SPALTEN) : undefined, maxH: d?.pc.max[1],
    };
  });

export const vonRgl = (items: readonly { i: string; x: number; y: number; w: number; h: number }[]): PcItem[] =>
  items.map(({ i, x, y, w, h }) => ({ id: i, x, y, w, h }));

/**
 * Handy: zwei Spalten — „gross“ volle Breite, „klein“ halbe Breite. Ein kleines Widget ohne
 * kleinen Nachbarn (nächstes Widget groß oder Listenende) nimmt die volle Breite ein.
 */
export function handyZeilen(items: MobilItem[]): { item: MobilItem; voll: boolean }[] {
  const out: { item: MobilItem; voll: boolean }[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.groesse === 'gross') { out.push({ item: it, voll: true }); continue; }
    const naechstes = items[i + 1];
    if (naechstes && naechstes.groesse === 'klein') {
      out.push({ item: it, voll: false }, { item: naechstes, voll: false });
      i++;
    } else {
      out.push({ item: it, voll: true });
    }
  }
  return out;
}

export function gleichesLayout(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}
